/**
 * Form values, client-side checks and saved `data` for a content item, all
 * driven by the field specs in collections.js.
 *
 * Paths are dotted, relative to `data`, and use list indexes, exactly like
 * the server's `error.field` without its 'data.' prefix (contract §1.2):
 * 'schedule.end', 'entries.2.phones.0'. An input's DOM id comes from its
 * path, so a server error can be put on, and focus, the right input.
 *
 * @module content/formModel
 */

import { isDateKey } from '../../../services/utils/dateUtils';

export const SCHEDULE_KINDS = Object.freeze([
  { value: 'cycle', label: 'Runs in cycles with dates' },
  { value: 'ongoing', label: 'Ongoing (no dates shown)' },
  { value: 'none', label: 'No dates, show the general note' },
]);

/** DOM id for the input at a path. */
export const fieldDomId = (path) => `cms-${String(path).replace(/[^a-zA-Z0-9_]+/g, '-')}`;

/* ------------------------------------------------------------ path utils */

const splitPath = (path) => (Array.isArray(path) ? path : String(path).split('.').filter(Boolean));

export const getIn = (obj, path) =>
  splitPath(path).reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);

/** Immutable set; numeric segments index arrays. */
export const setIn = (obj, path, value) => {
  const [head, ...rest] = splitPath(path);
  if (head === undefined) return value;
  const copy = Array.isArray(obj) ? obj.slice() : { ...(obj || {}) };
  copy[head] = setIn(copy[head], rest, value);
  return copy;
};

/** 'data.schedule.end' -> 'schedule.end'; other fields ('slug') unchanged. */
export const serverFieldToPath = (field) => {
  if (!field) return '';
  return field === 'data' ? '' : String(field).replace(/^data\./, '');
};

/* ---------------------------------------------------------- form values */

const blankSchedule = { kind: 'cycle', days: '', start: '', end: '', time: '' };

const toFormValue = (spec, raw) => {
  switch (spec.kind) {
    case 'checkbox':
      return raw === undefined ? Boolean(spec.default) : Boolean(raw);
    case 'choice':
      return raw || (spec.required ? '' : spec.options[0].value);
    case 'list': {
      const items = Array.isArray(raw) ? raw.map((v) => (v == null ? '' : String(v))) : [];
      return items.length === 0 && spec.minItems > 0 ? [''] : items;
    }
    case 'entries': {
      const items = Array.isArray(raw) ? raw.map((entry) => toFormValues(spec.fields, entry)) : [];
      return items.length === 0 && spec.minItems > 0 ? [toFormValues(spec.fields, {})] : items;
    }
    case 'schedule':
      return { ...blankSchedule, ...(raw || {}) };
    case 'image':
    case 'file':
      return raw || null;
    default:
      return raw == null ? '' : String(raw);
  }
};

/** Item `data` (or {} for a new item) -> form values. */
export const toFormValues = (fields, data = {}) => {
  const values = {};
  fields.forEach((spec) => {
    values[spec.key] = toFormValue(spec, data ? data[spec.key] : undefined);
  });
  return values;
};

/* ---------------------------------------------------------------- saving */

const cleanText = (spec, value) => {
  let text = String(value == null ? '' : value).replace(/\r\n?/g, '\n').trim();
  if (spec.normalize && text) text = spec.normalize(text);
  return text;
};

const mediaRef = (value) => (value ? { media_id: value.id || value.media_id } : null);

/** Form values -> the `data` object to save (§3.13.1 shape exactly). */
export const toPayload = (fields, values) => {
  const data = {};
  fields.forEach((spec) => {
    const value = values[spec.key];
    switch (spec.kind) {
      case 'checkbox':
        data[spec.key] = Boolean(value);
        return;
      case 'choice':
        data[spec.key] = value;
        return;
      case 'list': {
        const items = (value || []).map((v) => cleanText(spec.item, v));
        if (items.length === 0 && spec.empty === 'omit') return;
        data[spec.key] = items;
        return;
      }
      case 'entries':
        data[spec.key] = (value || []).map((entry) => toPayload(spec.fields, entry));
        return;
      case 'schedule': {
        const s = value || blankSchedule;
        data[spec.key] =
          s.kind === 'cycle'
            ? {
                kind: 'cycle',
                days: cleanText({}, s.days),
                start: s.start,
                end: s.end,
                time: cleanText({}, s.time),
              }
            : { kind: s.kind };
        return;
      }
      case 'image':
      case 'file':
        data[spec.key] = mediaRef(value);
        return;
      default: {
        const text = cleanText(spec, value);
        if (text === '' && spec.empty === 'omit') return;
        data[spec.key] = text === '' && spec.empty === 'null' ? null : text;
      }
    }
  });
  return data;
};

/* ---------------------------------------------------------------- checks */

const tooLong = (max, length) => `Keep this to ${max} characters or fewer. It has ${length} now.`;
const REQUIRED = 'Please fill this in.';

const checkText = (spec, value, siblings) => {
  const text = cleanText(spec, value);
  if (!text) {
    if (spec.required) return REQUIRED;
    return spec.check ? spec.check('', siblings) : '';
  }
  if (spec.max && text.length > spec.max) return tooLong(spec.max, text.length);
  if (spec.kind === 'date' && !isDateKey(text)) return 'Pick a date.';
  if (spec.pattern && !spec.pattern.test(text)) return spec.patternMessage;
  return spec.check ? spec.check(text, siblings) : '';
};

const checkSchedule = (s, path, errors) => {
  if (!s || s.kind !== 'cycle') return;
  const days = cleanText({}, s.days);
  const time = cleanText({}, s.time);
  if (!days) errors[`${path}.days`] = REQUIRED;
  else if (days.length > 40) errors[`${path}.days`] = tooLong(40, days.length);
  if (!isDateKey(s.start)) errors[`${path}.start`] = 'Pick the first day.';
  if (!isDateKey(s.end)) errors[`${path}.end`] = 'Pick the last day.';
  else if (isDateKey(s.start) && s.end < s.start) {
    errors[`${path}.end`] = 'The last day must be on or after the first day.';
  }
  if (!time) errors[`${path}.time`] = REQUIRED;
  else if (time.length > 40) errors[`${path}.time`] = tooLong(40, time.length);
};

/** Images need alt text: it lives on the picture, and the contract requires it. */
export const MISSING_ALT = 'This picture needs a description (alt text) before you can save.';

/**
 * Client-side checks mirroring §3.13.1. Returns {path: message} in field
 * order (the first key is the first problem on the page).
 */
export const validate = (fields, values, prefix = '') => {
  const errors = {};
  fields.forEach((spec) => {
    const path = `${prefix}${spec.key}`;
    const value = values[spec.key];
    switch (spec.kind) {
      case 'checkbox':
        return;
      case 'choice':
        if (spec.required && !spec.options.some((o) => o.value === value)) {
          errors[path] = spec.requiredMessage || 'Choose one.';
        }
        return;
      case 'list': {
        const items = value || [];
        items.forEach((item, i) => {
          const message = checkText(spec.item, item, values);
          if (message) errors[`${path}.${i}`] = message;
        });
        if (items.length < (spec.minItems || 0)) errors[path] = `Add at least ${spec.minItems}.`;
        else if (spec.maxItems && items.length > spec.maxItems) errors[path] = `Keep this to ${spec.maxItems} at most.`;
        return;
      }
      case 'entries': {
        const items = value || [];
        items.forEach((entry, i) => Object.assign(errors, validate(spec.fields, entry, `${path}.${i}.`)));
        if (items.length < (spec.minItems || 0)) errors[path] = `Add at least ${spec.minItems}.`;
        else if (spec.maxItems && items.length > spec.maxItems) errors[path] = `Keep this to ${spec.maxItems} at most.`;
        return;
      }
      case 'schedule':
        checkSchedule(value, path, errors);
        return;
      case 'image':
        if (!value) {
          if (!spec.nullable) errors[path] = 'Choose a picture.';
        } else if (value.kind && value.kind !== 'image') {
          errors[path] = 'Choose a picture, not a file.';
        } else if (value.id && !String(value.alt || '').trim()) {
          errors[path] = MISSING_ALT;
        }
        return;
      case 'file':
        if (!value) errors[path] = 'Choose a PDF file.';
        else if (value.kind && value.kind !== 'file') errors[path] = 'Choose a PDF file, not a picture.';
        return;
      default: {
        const message = checkText(spec, value, values);
        if (message) errors[path] = message;
      }
    }
  });
  return errors;
};

/* ------------------------------------------------------------ summaries */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** '2027-03-18' -> 'Mar 18, 2027' (a calendar day; no time zone involved). */
export const formatDayKey = (key) => {
  if (!isDateKey(key)) return key || '';
  const [y, m, d] = key.split('-').map(Number);
  return `${MONTHS[m - 1]} ${d}, ${y}`;
};

/** One line describing a schedule, e.g. 'Thursdays, Mar 18, 2027 to Apr 22, 2027, 6:00 to 7:30 PM'. */
export const describeSchedule = (schedule) => {
  if (!schedule) return '';
  if (schedule.kind === 'ongoing') return 'Ongoing';
  if (schedule.kind === 'none') return 'No dates';
  return [schedule.days, `${formatDayKey(schedule.start)} to ${formatDayKey(schedule.end)}`, schedule.time]
    .filter(Boolean)
    .join(', ');
};
